import { Injectable, inject } from '@angular/core';
import {
    RouterOutlet,
    Router,
    Params,
    ActivatedRoute,
    ChildrenOutletContexts,
    NavigationEnd
} from '@angular/router';

import * as _ from 'lodash';
import { Observable, BehaviorSubject } from 'rxjs';
@Injectable({
    providedIn: 'root'
})
export class BladeService {
    private _router = inject(Router);
    private _activeRoute = inject(ActivatedRoute);
    private _parentContexts = inject(ChildrenOutletContexts);

    public numberOfOutlets: number;
    public outletNames = [];
    public outlets: RouterOutlet[] = [];
    public allOutletParams$: Observable<Params>;

    private _allOutletParams$: BehaviorSubject<Params | undefined> = new BehaviorSubject(undefined);


    constructor() {
        this.allOutletParams$ = this._allOutletParams$.asObservable();
    }

    // initiliaze the blades
    public init(numberOfBlades = 3) {
        this.numberOfOutlets = numberOfBlades;
        this._init();
    }

    public addOutlet(outlet: RouterOutlet) {
        this.outlets.push(outlet);
    }

    public removeOutlet(outlet: RouterOutlet, name: string) {
        this.outlets = _.remove(this.outlets, outlet);
        const context = this._parentContexts.getContext(name);
        if (context) {
            context.route = undefined;
        }
    }

    public numberOfActiveBlades() {
        let numberOfBlades = 0;

        _.forEach(this.outlets, blade => {
            if (blade.isActivated) {
                numberOfBlades++;
            }
        });

        return numberOfBlades;
    }

    public closeBlade(bladeIndex: number) {
        const parentRoutes = this._activeRoute.children.slice(0, bladeIndex) as any;
        const routerLink = parentRoutes.map(x => x.url).flatMap(x => x.value).map(x => x.path);

        if (routerLink.length === 0) {
            const right0Segments = this._router.parseUrl(this._router.url)
                .root.children['outlet-right0']?.segments.map(s => s.path) ?? null;
            const right1Segments = this._router.parseUrl(this._router.url)
                .root.children['outlet-right1']?.segments.map(s => s.path) ?? null;
            const right2Segments = this._router.parseUrl(this._router.url)
                .root.children['outlet-right2']?.segments.map(s => s.path) ?? null;
            this._router.navigate(['', {
                outlets: { outlet0: null, outlet1: null, outlet2: null, 'outlet-right0': right0Segments, 'outlet-right1': right1Segments, 'outlet-right2': right2Segments }
            }]);
            return;
        }

        this._router.navigate(routerLink);
    }

    public getParentParams(outletName: string): Observable<Params> {
        const outlet = _.find(this.outlets, (o: any) => o.name === outletName);
        if (outlet === undefined) {
            return undefined;
        }

        const outletIndex = this.outlets.indexOf(outlet);
        const parentIndex = outletIndex - 1;
        if (parentIndex >= 0) {
            return this.outlets[parentIndex].activatedRoute.params;
        }
        return undefined;
    }

    private _getParamsAfterRouteEvent() {
        this._router.events.subscribe(x => {
            if (x instanceof NavigationEnd) {
                this._triggerParams();
            }
        });
    }

    private _init() {
        // set the outletNames
        this.outletNames = [];
        for (let index = 0; index < this.numberOfOutlets; index++) {
            this.outletNames.push(`outlet${index}`);
        }
        
        this._triggerParams();
        this._getParamsAfterRouteEvent();
    }

    // To get all the active parameters in the url
    private _triggerParams() {
        const activateOutlets = _.filter(this.outlets, (outlet: RouterOutlet) => outlet.isActivated);
        const allParams = _.map(activateOutlets, outlet => outlet.activatedRoute.snapshot.params);
        const allQueryParams = _.map(activateOutlets, outlet => outlet.activatedRoute.snapshot.queryParams);
        const allUrlSegments = _.map(activateOutlets, outlet => _.map(outlet.activatedRoute.snapshot.url, urlsegment=>urlsegment.path));
        let params = {};
        _.each(allParams, param => {
            params = {
                ...params,
                ...param
            };
        });
        _.each(allQueryParams, queryParam => {
            params = {
                ...params,
                ...queryParam
            };
        });
        _.each(allUrlSegments, urlSegment => {
            params = {
                ...params,
                paths: urlSegment
            };
        });
        this._allOutletParams$.next(params);
    }
}
